import { apiClient } from '@/shared/api/client'
import { secureStorage } from '@/shared/utils/secureStorage'
import { languageGoalApi } from '@/features/languagegoal/api/languageGoalApi'
import type { LearningPlanDto } from '@/features/languagegoal/types/learningPlan.types'
import type { DailyPlan } from '@/features/planner/types/planner.types'
import type { DashboardSummary } from '../types/dashboard.types'

const DEMO_DASHBOARD_SUMMARY: DashboardSummary = {
  totalGoals: 5,
  completedGoals: 2,
  activeGoals: 3,
  onHoldGoals: 0,
  totalTasks: 18,
  completedTasks: 12,
  pendingTasks: 5,
  blockedTasks: 1,
  overallProgressPercentage: 68,
  currentStreak: 6,
  longestStreak: 14,
  todayPlannedTasks: 5,
  todayCompletedTasks: 3,
  productivityScore: 84,
  deadlineSummary: {
    overdueTasksCount: 0,
    tasksDueTodayCount: 2,
    tasksDueThisWeekCount: 4,
    upcomingGoalDeadlinesCount: 1,
  },
  learningSummary: {
    hasActiveLanguageGoal: true,
    objectiveType: 'CELPIP_GENERAL',
    targetExam: 'CELPIP-General',
    targetScore: 9,
    currentEstimatedScore: 8,
    confidence: 'ASSESSED',
    nextRecommendedAction: 'Part 3 Listening Practice: Conversation Analysis drill (20 min)',
    completedActivitiesCount: 14,
    dailyTargetMinutes: 60,
    timeSpentTodayMinutes: 35,
    hasFrenchProfile: true,
    frenchTargetLevel: 'NCLC 7',
    frenchTargetExam: 'TEF Canada',
    frenchPendingTasksToday: 1,
  },
  recentGoals: [
    {
      id: 'goal-1',
      title: 'CELPIP-General CLB 9 Preparation',
      description: 'Achieve CLB 9 across all four components: Listening, Reading, Writing, Speaking for Express Entry',
      category: 'LANGUAGE',
      status: 'IN_PROGRESS',
      priority: 'HIGH',
      progressPercentage: 75,
      taskCount: 8,
      completedTaskCount: 6,
      startDate: null,
      targetDate: new Date(Date.now() + 60 * 86400000).toISOString().split('T')[0],
      createdAt: new Date(Date.now() - 14 * 86400000).toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'goal-2',
      title: 'French NCLC 7 / CEFR B2 Fast Track',
      description: 'Master oral comprehension and structured argumentation for TEF Canada',
      category: 'LANGUAGE',
      status: 'IN_PROGRESS',
      priority: 'HIGH',
      progressPercentage: 60,
      taskCount: 6,
      completedTaskCount: 3,
      startDate: null,
      targetDate: new Date(Date.now() + 90 * 86400000).toISOString().split('T')[0],
      createdAt: new Date(Date.now() - 21 * 86400000).toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'goal-3',
      title: 'Software System Architecture Review',
      description: 'Refactor high-throughput distributed messaging and documentation',
      category: 'CAREER',
      status: 'IN_PROGRESS',
      priority: 'MEDIUM',
      progressPercentage: 40,
      taskCount: 5,
      completedTaskCount: 2,
      startDate: null,
      targetDate: null,
      createdAt: new Date(Date.now() - 30 * 86400000).toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ],
  upcomingTasks: [
    {
      id: 'task-1',
      title: 'Complete CELPIP Listening Task 3 practice test',
      description: 'Listening comprehension drill',
      status: 'IN_PROGRESS',
      priority: 'HIGH',
      dueDate: new Date().toISOString().split('T')[0],
      estimatedHours: 1,
      actualHours: 0.5,
      completionPercentage: 50,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      goalId: 'goal-1',
    },
    {
      id: 'task-2',
      title: 'Review French subjunctive irregular verbs flashcard deck',
      description: 'Grammar review for TEF Canada',
      status: 'TODO',
      priority: 'MEDIUM',
      dueDate: new Date().toISOString().split('T')[0],
      estimatedHours: 1,
      actualHours: 0,
      completionPercentage: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      goalId: 'goal-2',
    },
    {
      id: 'task-3',
      title: 'Draft timed essay: CELPIP Writing Task 2 survey response',
      description: 'Timed writing test practice',
      status: 'TODO',
      priority: 'HIGH',
      dueDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      estimatedHours: 1.5,
      actualHours: 0,
      completionPercentage: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      goalId: 'goal-1',
    },
  ],
  recentActivities: [
    {
      id: 'act-1',
      eventType: 'TASK_COMPLETED',
      metadata: { taskTitle: 'CELPIP Reading Part 2: Applying a Diagram drill' },
      createdAt: new Date(Date.now() - 2 * 3600000).toISOString(),
    },
    {
      id: 'act-2',
      eventType: 'DAILY_PLAN_CREATED',
      metadata: { planDate: new Date().toISOString().split('T')[0] },
      createdAt: new Date(Date.now() - 5 * 3600000).toISOString(),
    },
    {
      id: 'act-3',
      eventType: 'GOAL_CREATED',
      metadata: { goalTitle: 'French NCLC 7 / CEFR B2 Fast Track' },
      createdAt: new Date(Date.now() - 24 * 3600000).toISOString(),
    },
    {
      id: 'act-4',
      eventType: 'DAILY_PLAN_COMPLETED',
      metadata: { planDate: 'yesterday' },
      createdAt: new Date(Date.now() - 26 * 3600000).toISOString(),
    },
  ],
}

function buildDynamicDashboardSummary(): DashboardSummary {
  const todayStr = new Date().toISOString().split('T')[0]
  const goal = languageGoalApi.getStoredGoal()
  const plan = secureStorage.getItem<LearningPlanDto>('nttd_celpip_learning_plan')
  const dailyPlan = secureStorage.getItem<DailyPlan>(`nttd_daily_plan_${todayStr}`)

  const assessedSkills = (goal?.skills || []).filter(
    (s: any) => s.baselineStatus === 'ASSESSED' && s.baselineScore != null
  )
  const hasAssessed = assessedSkills.length > 0
  const currentEstimatedScore = hasAssessed
    ? Math.round(
        (assessedSkills.reduce((acc: number, s: any) => acc + Number(s.baselineScore), 0) /
          assessedSkills.length) *
          10
      ) / 10
    : undefined

  const nextPendingActivity = plan?.plannedActivities?.find((a: any) => !a.completed)
  const nextRecommendedAction = nextPendingActivity
    ? `Scheduled ${nextPendingActivity.skill}: ${nextPendingActivity.title} (${nextPendingActivity.estimatedDurationMinutes}m)`
    : hasAssessed
    ? 'Continue regular drill practice across all CLB components'
    : 'Complete diagnostic assessment to calibrate learning plan'

  const completedActivitiesCount = plan?.completedCount ?? 0
  const todayPlannedTasks = dailyPlan?.items?.length ?? DEMO_DASHBOARD_SUMMARY.todayPlannedTasks
  const todayCompletedTasks = dailyPlan?.items?.filter((i: any) => i.completed)?.length ?? DEMO_DASHBOARD_SUMMARY.todayCompletedTasks
  const productivityScore =
    todayPlannedTasks > 0 ? Math.round((todayCompletedTasks / todayPlannedTasks) * 100) : 100

  return {
    ...DEMO_DASHBOARD_SUMMARY,
    todayPlannedTasks,
    todayCompletedTasks,
    productivityScore,
    learningSummary: {
      hasActiveLanguageGoal: !!goal,
      objectiveType: goal?.objectiveType || 'CELPIP_GENERAL',
      targetExam: goal?.objectiveType === 'CELPIP_GENERAL' ? 'CELPIP-General' : 'French TEF/TCF',
      targetScore: goal?.skills?.[0]?.targetScore ?? 9,
      currentEstimatedScore,
      confidence: plan?.planConfidence || (hasAssessed ? 'ASSESSED' : 'PROVISIONAL'),
      nextRecommendedAction,
      completedActivitiesCount,
      dailyTargetMinutes: goal?.dailyStudyMinutes || 60,
      timeSpentTodayMinutes: 0,
      hasFrenchProfile: true,
      frenchTargetLevel: 'NCLC 7',
      frenchTargetExam: 'TEF Canada',
      frenchPendingTasksToday: 0,
    },
  }
}

/**
 * Dashboard API client.
 * Calls backend API with seamless fallback when offline or in demo mode.
 */
export const dashboardApi = {
  getSummary: async (): Promise<DashboardSummary> => {
    try {
      const { data } = await apiClient.get<DashboardSummary>('/api/dashboard/summary')
      if (data && typeof data === 'object') {
        return data
      }
    } catch {
      // Backend not running or demo session
    }
    return buildDynamicDashboardSummary()
  },
}
